import React, { createContext, useContext, useId, useRef, useState } from 'react';
import P from '@hero/ui-kit/typography/P';
import H from '@hero/ui-kit/typography/H';
import { MarkButton } from '../MarkControls';
import type { Block, Channel } from '../../types';
import { useEditorContext } from './EditorContext';
import { NESTING_LIMIT, SMS_ALLOWED_BLOCKS } from './constants';
import { CONTENT_CHOICES, hasContentErrors } from './contentChoices';
import ContentPicker from './ContentPicker';
import EditableBlock from './EditableBlock';
import BlockRenderer from './BlockRenderer';
import * as styles from './canvas.module.css';
import * as layout from '../markLayout.module.css';

type Props = {
    blocks: Block[];
    onChange: (blocks: Block[]) => void;
    channel: Channel;
    depth: number;
    label?: string;
};
type ListState = {
    ids: string[];
    deleted: { block: Block; id: string; index: number }[];
    nextId: number;
};
const SelectionContext = createContext<{
    selected: string | null;
    select: (path: string | null) => void;
    lists: React.MutableRefObject<Map<string, ListState>>;
} | null>(null);
const ParentPathContext = createContext('');
const CanvasBlockList: React.FC<Props> = (props) => {
    const inherited = useContext(SelectionContext);
    const [selected, select] = useState<string | null>(null);
    const lists = useRef(new Map<string, ListState>());
    return inherited ? (
        <CanvasList {...props} />
    ) : (
        <SelectionContext.Provider value={{ selected, select, lists }}>
            <CanvasList {...props} />
        </SelectionContext.Provider>
    );
};
const CanvasList: React.FC<Props> = ({ blocks, onChange, channel, depth, label }) => {
    const { selected, select, lists } = useContext(SelectionContext)!;
    const parentPath = useContext(ParentPathContext);
    const { setInsertTarget, showErrors, readOnly, validation } = useEditorContext();
    const scope = useId();
    const [expandedSectionId, setExpandedSectionId] = useState<string | null>(null);
    // Keep branch identities and Undo history for the lifetime of the canvas,
    // including while a deleted ancestor has unmounted its nested editors.
    const listKey = JSON.stringify([parentPath, label ?? null]);
    const [listState, setListState] = useState(() => {
        const stored = lists.current.get(listKey);
        if (stored) return stored;
        const initial: ListState = {
            ids: blocks.map((_, index) => `${scope}-${index}`),
            deleted: [],
            nextId: blocks.length
        };
        lists.current.set(listKey, initial);
        return initial;
    });
    const { ids, deleted } = listState;
    const container = useRef<HTMLDivElement>(null);
    const announceRef = useRef<HTMLDivElement>(null);
    const path = (id: string) => `${parentPath}/${id}`;
    // Reordering keeps the selected path; leaving it resets the disclosure.
    if (expandedSectionId !== null && selected !== path(expandedSectionId)) {
        setExpandedSectionId(null);
    }
    const announce = (message: string) => {
        if (announceRef.current) announceRef.current.textContent = message;
    };
    const focusSection = (id: string, edit: boolean) =>
        requestAnimationFrame(() =>
            requestAnimationFrame(() => {
                const section = Array.from(
                    container.current?.querySelectorAll<HTMLElement>('[data-content-id]') ?? []
                ).find((element) => element.dataset.contentId === id);
                const field = edit
                    ? Array.from(
                          section?.querySelectorAll<HTMLElement>(
                              'textarea:not(:disabled), input:not(:disabled), select:not(:disabled)'
                          ) ?? []
                      ).find((element) => !element.closest('[hidden]'))
                    : null;
                (
                    field ??
                    section?.querySelector<HTMLElement>('[data-edit-section]') ??
                    container.current?.querySelector<HTMLElement>('button')
                )?.focus();
            })
        );
    const commit = (
        next: Block[],
        nextIds: string[],
        nextDeleted = deleted,
        nextId = listState.nextId
    ) => {
        setInsertTarget(null);
        const updated = { ids: nextIds, deleted: nextDeleted, nextId };
        lists.current.set(listKey, updated);
        setListState(updated);
        onChange(next);
    };
    const insert = (index: number, block: Block) => {
        const id = `${scope}-${listState.nextId}`;
        commit(
            [...blocks.slice(0, index), block, ...blocks.slice(index)],
            [...ids.slice(0, index), id, ...ids.slice(index)],
            deleted,
            listState.nextId + 1
        );
        select(path(id));
        focusSection(id, true);
        announce(`${CONTENT_CHOICES[block.type].label} added. Editing section ${index + 1}.`);
    };
    const move = (index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (target < 0 || target >= blocks.length) return;
        const next = [...blocks],
            nextIds = [...ids];
        [next[index], next[target]] = [next[target], next[index]];
        [nextIds[index], nextIds[target]] = [nextIds[target], nextIds[index]];
        commit(next, nextIds);
        focusSection(ids[index], false);
        announce(`Section moved to position ${target + 1}.`);
    };
    const remove = (index: number) => {
        setExpandedSectionId(null);
        commit(
            blocks.filter((_, i) => i !== index),
            ids.filter((_, i) => i !== index),
            [...deleted, { block: blocks[index], id: ids[index], index }]
        );
        select(parentPath || null);
        requestAnimationFrame(() =>
            container.current?.querySelector<HTMLElement>('[data-undo]')?.focus()
        );
        announce('Section deleted. Undo is available.');
    };
    if (depth > NESTING_LIMIT)
        return (
            <P noDefaultMargin size="small">
                This layout cannot be nested any further.
            </P>
        );
    return (
        <div
            ref={container}
            className={depth === 0 ? styles.canvas : styles.nested}
            data-template-canvas={depth === 0 ? true : undefined}
        >
            <div ref={announceRef} aria-live="polite" className={layout.srOnly} />
            {label && (
                <P noDefaultMargin strong styles={{ marginBottom: '1.6rem' }}>
                    {label}
                </P>
            )}
            {deleted.length > 0 && !readOnly && (
                <div className={styles.undo} role="status">
                    <P noDefaultMargin>Section deleted.</P>
                    <MarkButton
                        data-undo
                        size="regular"
                        onClick={() => {
                            const item = deleted[deleted.length - 1];
                            const index = Math.min(item.index, blocks.length);
                            commit(
                                [...blocks.slice(0, index), item.block, ...blocks.slice(index)],
                                [...ids.slice(0, index), item.id, ...ids.slice(index)],
                                deleted.slice(0, -1)
                            );
                            select(path(item.id));
                            focusSection(item.id, true);
                            announce('Deleted section restored.');
                        }}
                    >
                        Undo
                    </MarkButton>
                </div>
            )}
            {blocks.length === 0 && (
                <div className={styles.empty}>
                    <H role="h6" noDefaultMargin>
                        {depth === 0 ? 'Start your email' : 'Add content to this section'}
                    </H>
                    <P noDefaultMargin size="small">
                        {readOnly
                            ? 'There is no content in this section.'
                            : 'Add text, a heading, an image, or a button.'}
                    </P>
                    {!readOnly && (
                        <ContentPicker
                            channel={channel}
                            depth={depth}
                            position={1}
                            parentLabel={label}
                            showLabel
                            onInsert={(block) => insert(0, block)}
                        />
                    )}
                </div>
            )}
            {blocks.length > 0 && !readOnly && (
                <div className={styles.insert}>
                    <ContentPicker
                        triggerLabel="Add content at the start"
                        channel={channel}
                        depth={depth}
                        position={1}
                        parentLabel={label}
                        onInsert={(block) => insert(0, block)}
                    />
                </div>
            )}
            {blocks.map((block, index) => {
                const id = ids[index];
                const currentPath = path(id);
                const editing =
                    !readOnly &&
                    (selected === currentPath || selected?.startsWith(`${currentPath}/`));
                const smsDisallowed =
                    channel === 'sms' &&
                    !(SMS_ALLOWED_BLOCKS as readonly string[]).includes(block.type);
                const invalid = !!showErrors && hasContentErrors(block, validation);
                return (
                    <React.Fragment key={id}>
                        <section
                            data-content-id={id}
                            data-content-invalid={invalid || undefined}
                            aria-label={`${CONTENT_CHOICES[block.type].label} section ${index + 1}`}
                            className={[
                                styles.section,
                                editing && styles.selected,
                                invalid && styles.invalid
                            ]
                                .filter(Boolean)
                                .join(' ')}
                        >
                            <div className={styles.sectionHeader}>
                                <P noDefaultMargin size="small" strong>
                                    {CONTENT_CHOICES[block.type].label}
                                </P>
                                {!readOnly && (
                                    <MarkButton
                                        data-edit-section
                                        aria-label={`Edit ${CONTENT_CHOICES[block.type].label.toLowerCase()} section ${index + 1}`}
                                        aria-expanded={!!editing}
                                        aria-controls={`${id}-editor`}
                                        onClick={() => {
                                            setExpandedSectionId(null);
                                            setInsertTarget(null);
                                            select(currentPath);
                                            focusSection(id, true);
                                        }}
                                    >
                                        Edit
                                    </MarkButton>
                                )}
                            </div>
                            {smsDisallowed && (
                                <P
                                    noDefaultMargin
                                    size="small"
                                    styles={{ color: 'var(--mark-danger)' }}
                                >
                                    This content is available for email only.
                                </P>
                            )}
                            <div id={`${id}-editor`} hidden={!editing}>
                                <div className={styles.editor}>
                                    <fieldset className={layout.fieldset} disabled={smsDisallowed}>
                                        <ParentPathContext.Provider value={currentPath}>
                                            <EditableBlock
                                                block={block}
                                                depth={depth}
                                                onChange={(updated) =>
                                                    onChange(
                                                        blocks.map((item, i) =>
                                                            i === index ? updated : item
                                                        )
                                                    )
                                                }
                                            />
                                        </ParentPathContext.Provider>
                                    </fieldset>
                                    {selected === currentPath && (
                                        <div
                                            className={`${styles.actions} ${styles.editorActions}`}
                                        >
                                            <MarkButton
                                                compact
                                                variant="primary"
                                                onClick={() => {
                                                    setExpandedSectionId(null);
                                                    setInsertTarget(null);
                                                    select(parentPath || null);
                                                    focusSection(id, false);
                                                }}
                                            >
                                                Done editing
                                            </MarkButton>
                                            <MarkButton
                                                compact
                                                aria-expanded={expandedSectionId === id}
                                                aria-controls={`${id}-actions`}
                                                onClick={() =>
                                                    setExpandedSectionId((current) =>
                                                        current === id ? null : id
                                                    )
                                                }
                                            >
                                                More actions
                                            </MarkButton>
                                            <div
                                                id={`${id}-actions`}
                                                className={styles.actionGroup}
                                            >
                                                {expandedSectionId === id && (
                                                    <>
                                                        <MarkButton
                                                            compact
                                                            disabled={index === 0}
                                                            onClick={() => move(index, -1)}
                                                        >
                                                            Move up
                                                        </MarkButton>
                                                        <MarkButton
                                                            compact
                                                            disabled={index === blocks.length - 1}
                                                            onClick={() => move(index, 1)}
                                                        >
                                                            Move down
                                                        </MarkButton>
                                                        <MarkButton
                                                            compact
                                                            disabled={smsDisallowed}
                                                            onClick={() =>
                                                                insert(
                                                                    index + 1,
                                                                    JSON.parse(
                                                                        JSON.stringify(block)
                                                                    ) as Block
                                                                )
                                                            }
                                                        >
                                                            Duplicate
                                                        </MarkButton>
                                                        <MarkButton
                                                            compact
                                                            tone="danger"
                                                            onClick={() => remove(index)}
                                                        >
                                                            Delete
                                                        </MarkButton>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div hidden={!!editing}>
                                <div className={styles.preview}>
                                    <CanvasPreview block={block} depth={depth} />
                                </div>
                            </div>
                        </section>
                        {!readOnly && (
                            <div className={styles.insert}>
                                <ContentPicker
                                    triggerLabel={
                                        index === blocks.length - 1
                                            ? '+ Add content'
                                            : '+ Add content here'
                                    }
                                    channel={channel}
                                    depth={depth}
                                    position={index + 2}
                                    parentLabel={label}
                                    showLabel={index === blocks.length - 1}
                                    onInsert={(newBlock) => insert(index + 1, newBlock)}
                                />
                            </div>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};
const CanvasPreview: React.FC<{ block: Block; depth: number }> = ({ block, depth }) => {
    const { variables } = useEditorContext();
    if (
        (block.type === 'heading' || block.type === 'paragraph' || block.type === 'button') &&
        !block.text.trim()
    )
        return (
            <P noDefaultMargin styles={{ color: 'var(--mark-muted)' }}>
                {block.type === 'heading'
                    ? 'Your heading will appear here.'
                    : block.type === 'button'
                      ? 'Your button will appear here.'
                      : 'Your message will appear here.'}
            </P>
        );
    if (block.type === 'image' && !block.src.trim())
        return (
            <P noDefaultMargin styles={{ color: 'var(--mark-muted)' }}>
                Add an image link to show your image.
            </P>
        );
    if (block.type === 'conditional')
        return (
            <>
                <P
                    noDefaultMargin
                    size="small"
                    styles={{ color: 'var(--mark-muted)', marginBottom: '0.8rem' }}
                >
                    {block.variable
                        ? `Shown when ${variables.find((variable) => variable.key === block.variable)?.label ?? block.variable} ${block.operator === 'exists' ? 'is provided' : block.operator === 'neq' ? `is not ${block.value ?? ''}` : `is ${block.value ?? ''}`}.`
                        : 'Choose when this content should appear.'}
                </P>
                <BlockRenderer blocks={block.children} depth={depth + 1} />
                {!!block.else_children?.length && (
                    <>
                        <P noDefaultMargin size="small">
                            Otherwise
                        </P>
                        <BlockRenderer blocks={block.else_children} depth={depth + 1} />
                    </>
                )}
            </>
        );
    return <BlockRenderer blocks={[block]} depth={depth} />;
};
export default CanvasBlockList;
